import React, { useState } from 'react';
import { useLanguage } from '@/context/language-context';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { EscortProfile } from '@/types';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { 
  Eye, 
  EyeOff, 
  Globe, 
  Lock, 
  Calendar, 
  UserCheck, 
  AlertTriangle 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface VisibilityTabProps {
  profile: EscortProfile;
  onProfileUpdate: (profile: EscortProfile) => void;
}

const VisibilityTab: React.FC<VisibilityTabProps> = ({ profile, onProfileUpdate }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [settings, setSettings] = useState({
    isOnline: profile.isOnline || false,
    isTouring: profile.isTouring || false
  });

  const handleToggle = (key: 'isOnline' | 'isTouring') => {
    setSettings(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('escort_profiles')
        .update({
          is_online: settings.isOnline,
          is_touring: settings.isTouring
        })
        .eq('id', profile.id);

      if (error) throw error;

      onProfileUpdate({
        ...profile,
        isOnline: settings.isOnline,
        isTouring: settings.isTouring
      });

      toast({
        title: t('escort.profile.visibility_updated'),
        description: t('escort.profile.visibility_update_success'),
      });
    } catch (error) {
      console.error('Error updating visibility settings:', error);
      toast({
        title: t('escort.profile.update_error'),
        description: t('escort.profile.try_again_later'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold">{t('escort.profile.visibility_settings')}</h2>
        <p className="text-muted-foreground">{t('escort.profile.visibility_description')}</p>
      </div>

      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              <CardTitle>{t('escort.profile.online_status')}</CardTitle>
            </div>
            <CardDescription>
              {t('escort.profile.online_status_description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {settings.isOnline ? (
                  <Eye className="h-5 w-5 text-green-500" />
                ) : (
                  <EyeOff className="h-5 w-5 text-gray-500" />
                )}
                <span>
                  {settings.isOnline
                    ? t('escort.profile.visible_to_clients')
                    : t('escort.profile.invisible_to_clients')}
                </span>
              </div>
              <Switch
                checked={settings.isOnline}
                onCheckedChange={() => handleToggle('isOnline')}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              <CardTitle>{t('escort.profile.touring_status')}</CardTitle>
            </div>
            <CardDescription>
              {t('escort.profile.touring_status_description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {settings.isTouring ? (
                  <Globe className="h-5 w-5 text-blue-500" />
                ) : (
                  <Lock className="h-5 w-5 text-gray-500" />
                )}
                <span>
                  {settings.isTouring
                    ? t('escort.profile.touring_active')
                    : t('escort.profile.touring_inactive')}
                </span>
              </div>
              <Switch
                checked={settings.isTouring}
                onCheckedChange={() => handleToggle('isTouring')}
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-primary" />
              <CardTitle>{t('escort.profile.verification_status')}</CardTitle>
            </div>
            <CardDescription>
              {t('escort.profile.verification_status_description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {profile.isVerified ? (
                  <UserCheck className="h-5 w-5 text-green-500" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                )}
                <span>
                  {profile.isVerified
                    ? t('escort.profile.verified_account')
                    : t('escort.profile.not_verified')}
                </span>
              </div>
              {!profile.isVerified && (
                <Button variant="outline" size="sm" onClick={() => navigate('/escort-dashboard/security')}>
                  {t('escort.profile.request_verification')}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardFooter className="border-t p-6">
            <Button
              className="ml-auto"
              onClick={saveSettings}
              disabled={isSaving}
            >
              {isSaving ? t('escort.profile.saving') : t('escort.profile.save_changes')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default VisibilityTab;
